import { useMemo, useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { AppShell } from '@/components/AppShell';
import { useFeed } from '@/contexts/FeedContext';
import { useArticles } from '@/hooks/useFeeds';
import { UserAvatar } from '@/components/user';
import { useAuthor } from '@/hooks/useAuthor';
import { getDisplayName } from '@/lib/getDisplayName';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { getTagValue, timeAgo } from '@/lib/jumble';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Composer } from '@/components/Composer';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { LoginArea } from '@/components/auth/LoginArea';
import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';

export function ArticlesPage() {
  useSeoMeta({ title: 'Long-form articles — Jumble', description: 'Read NIP-23 long-form articles from Nostr relays.' });
  const { relaySets, feedRelays } = useFeed();
  const [setId, setTab] = useState<string>(relaySets[0]?.id ?? 'global');
  const set = relaySets.find((r) => r.id === setId);
  const urls = set?.relayUrls ?? feedRelays;
  const articles = useArticles(urls);

  return (
    <AppShell title="Articles">
      <Tabs value={setId} onValueChange={setTab}>
        <TabsList className="w-full justify-start overflow-x-auto rounded-none border-b bg-transparent">
          {relaySets.map((s) => (
            <TabsTrigger key={s.id} value={s.id}>{s.name}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={setId} className="mt-0">
          {articles.isLoading ? (
            <div className="space-y-2 p-4"><Skeleton className="h-24 w-full" /><Skeleton className="h-24 w-full" /></div>
          ) : (articles.data ?? []).length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">No long-form articles found on these relays.</p>
          ) : (
            (articles.data ?? []).map((e) => <ArticleRow key={e.id} event={e} />)
          )}
        </TabsContent>
      </Tabs>
      <div className="pb-20 md:pb-4" />
    </AppShell>
  );
}

function ArticleRow({ event }: { event: NostrEvent }) {
  const title = getTagValue(event, 'title') ?? 'Untitled';
  const summary = getTagValue(event, 'summary');
  const image = getTagValue(event, 'image');
  const d = getTagValue(event, 'd') ?? '';
  let naddr = '';
  try {
    naddr = nip19.naddrEncode({ kind: event.kind, pubkey: event.pubkey, identifier: d });
  } catch { naddr = ''; }
  return (
    <Link to={naddr ? `/article/${naddr}` : '#'} className="flex gap-3 border-b p-4 transition-colors hover:bg-accent/30">
      {image && <img src={image} alt="" className="h-20 w-28 shrink-0 rounded-lg object-cover" loading="lazy" />}
      <div className="min-w-0 flex-1">
        <p className="truncate font-bold">{title}</p>
        {summary && <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{summary}</p>}
        <ArticleMeta event={event} />
      </div>
    </Link>
  );
}

export function ArticleMeta({ event }: { event: NostrEvent }) {
  const author = useAuthor(event.pubkey);
  return (
    <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
      <UserAvatar pubkey={event.pubkey} size="xs" />
      <span className="truncate">{getDisplayName(author.data?.metadata, event.pubkey)}</span>
      <span>·</span><span>{timeAgo(event.created_at)}</span>
    </p>
  );
}

export function ArticlePage({ naddr }: { naddr: string }) {
  const { nostr } = useNostr();
  const parsed = useMemo(() => {
    try {
      const d = nip19.decode(naddr);
      if (d.type !== 'naddr') return null;
      return d.data as { kind: number; pubkey: string; identifier: string };
    } catch { return null; }
  }, [naddr]);

  const query = useQuery({
    queryKey: ['jumble', 'article', naddr],
    enabled: !!parsed,
    queryFn: async ({ signal }) => {
      const [e] = (await nostr.query(
        [{ kinds: [parsed!.kind], authors: [parsed!.pubkey], '#d': [parsed!.identifier] }],
        { signal: signal ?? AbortSignal.timeout(4000) },
      )) as NostrEvent[];
      if (!e) throw new Error('Article not found');
      return e;
    },
  });

  const title = query.data ? getTagValue(query.data, 'title') ?? 'Untitled' : 'Article';
  useSeoMeta({ title: `${title} — Jumble`, description: (query.data && getTagValue(query.data, 'summary')) ?? 'Nostr long-form article.' });

  return (
    <AppShell title="Article">
      {query.isLoading && <div className="space-y-2 p-4"><Skeleton className="h-8 w-2/3" /><Skeleton className="h-64 w-full" /></div>}
      {query.isError && <p className="p-8 text-center text-sm text-muted-foreground">Article not found.</p>}
      {query.data && (
        <article className="mx-auto max-w-2xl p-4 pb-20 md:pb-8">
          <h1 className="text-3xl font-black tracking-tight">{title}</h1>
          <div className="mt-2"><ArticleMeta event={query.data} /></div>
          {getTagValue(query.data, 'image') && <img src={getTagValue(query.data, 'image')} alt="" className="mt-4 w-full rounded-xl" />}
          <div className="mt-4 max-w-none whitespace-pre-wrap break-words text-[17px] leading-relaxed">
            {query.data.content}
          </div>
        </article>
      )}
    </AppShell>
  );
}

export function FollowingPage() {
  useSeoMeta({ title: 'Following — Jumble', description: 'Notes from people you follow.' });
  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const follows = useQuery({
    queryKey: ['jumble', 'follows-page', user?.pubkey],
    enabled: !!user,
    queryFn: async ({ signal }) => {
      const [list] = await nostr.query([{ kinds: [3], authors: [user!.pubkey], limit: 1 }], { signal: signal ?? AbortSignal.timeout(3000) });
      return (list?.tags ?? []).filter((t) => t[0] === 'p').map((t) => t[1]);
    },
  });
  return (
    <AppShell title="Following">
      {!user ? (
        <div className="flex flex-col items-center gap-3 p-10"><p className="text-muted-foreground">Log in to see who you follow.</p><LoginArea /></div>
      ) : (
        <div className="pb-20 md:pb-4">
          <div className="border-b p-4"><Composer placeholder="Post to your followers…" /></div>
          <p className="border-b px-4 py-2 text-sm text-muted-foreground">Following {follows.data?.length ?? '…'} people</p>
          {(follows.data ?? []).map((pk) => <FollowRow key={pk} pubkey={pk} />)}
        </div>
      )}
    </AppShell>
  );
}

function FollowRow({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  return (
    <Link to={`/${pubkeyToNpub(pubkey)}`} className="flex items-center gap-3 border-b px-4 py-3 hover:bg-accent/40">
      <UserAvatar pubkey={pubkey} />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold">{getDisplayName(metadata, pubkey)}</span>
        {metadata?.about && <span className="block truncate text-sm text-muted-foreground">{metadata.about}</span>}
      </span>
    </Link>
  );
}
